import { ChevronDown } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";

import { docsPath } from "@/lib/docs-pages";

import {
  type DocBlocks,
  type DocEntry,
  FAQ_IDS,
  type FaqEntry,
  type FaqId,
  useDocsContent,
} from "./content";
import { FOCUS_DOC_TITLE } from "./focus";
import { emphasis, pageLinks } from "./utils";

/**
 * Docs text with its `**…**` runs in bold and its `[[<page>]]` links as links
 * to that page, named by its title in the dashboard's language. Like the
 * previous and next links, they move focus to the title of the page they open.
 */
export function RichText({ text }: { text: string }) {
  const { pages } = useDocsContent();
  return (
    <>
      {pageLinks(text).map((run, index) =>
        typeof run === "string" ? (
          <Fragment key={index}>
            {emphasis(run).map((part, partIndex) =>
              part.strong ? <strong key={partIndex}>{part.text}</strong> : part.text,
            )}
          </Fragment>
        ) : (
          <Link
            key={index}
            to={docsPath(run.page)}
            state={FOCUS_DOC_TITLE}
            className="text-accent font-medium hover:underline"
          >
            {pages[run.page]}
          </Link>
        ),
      )}
    </>
  );
}

/** A heading inside a page. */
export function DocSubheading({ children }: { children: ReactNode }) {
  return <h3 className="text-foreground pt-2 text-base font-semibold">{children}</h3>;
}

/** Numbered steps: an `<ol>` of `<Step>`s. */
export function Steps({ children }: { children: ReactNode }) {
  return <ol className="space-y-3">{children}</ol>;
}

/** One step, in De Ceuster's style: a white number in a blue circle. */
export function Step({ number, children }: { number: number; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden="true"
        className="bg-primary text-primary-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold"
      >
        {number}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}

interface EntryListProps<Id extends string> {
  /** The entries' ids, in the order to show them. */
  ids: readonly Id[];
  entries: Record<Id, DocEntry>;
}

/** A bulleted list of points, each with its lead in bold. */
export function EntryList<Id extends string>({ ids, entries }: EntryListProps<Id>) {
  return (
    <ul className="list-disc space-y-2 pl-5">
      {ids.map((id) => (
        <li key={id}>
          <strong className="font-semibold">{entries[id].title}.</strong>{" "}
          <RichText text={entries[id].body} />
        </li>
      ))}
    </ul>
  );
}

/** Paragraphs, and a bulleted list where a block is a list of strings. */
function Blocks({ blocks }: { blocks: DocBlocks }) {
  return (
    <>
      {blocks.map((block, index) =>
        typeof block === "string" ? (
          <p key={index}>
            <RichText text={block} />
          </p>
        ) : (
          <ul key={index} className="list-disc space-y-1 pl-5">
            {block.map((point, pointIndex) => (
              <li key={pointIndex}>
                <RichText text={point} />
              </li>
            ))}
          </ul>
        ),
      )}
    </>
  );
}

/**
 * The line that opens a `<details>` of the docs (put `group` on the
 * `<details>`): its text in semibold and a chevron that turns when it is open.
 * Native, so it opens with the keyboard and screen readers say whether it is
 * open.
 */
function DetailsSummary({ children }: { children: ReactNode }) {
  return (
    <summary className="focus-visible:ring-ring flex cursor-pointer list-none items-center justify-between gap-3 rounded-sm py-3 font-semibold focus-visible:ring-2 focus-visible:outline-none [&::-webkit-details-marker]:hidden">
      {children}
      <ChevronDown
        className="text-muted size-4 shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none"
        aria-hidden="true"
      />
    </summary>
  );
}

/**
 * The FAQ: each question opens its answer (native `<details>`, so keyboard and
 * screen readers work). Keyed by the question's id, so an open answer stays
 * open when the language switches.
 */
export function FaqList({ entries }: { entries: Record<FaqId, FaqEntry> }) {
  return (
    <div className="border-border divide-border divide-y rounded-lg border">
      {FAQ_IDS.map((id) => (
        <details key={id} className="group px-4">
          <DetailsSummary>{entries[id].question}</DetailsSummary>
          <div className="text-foreground space-y-2 pb-4">
            <Blocks blocks={entries[id].answer} />
          </div>
        </details>
      ))}
    </div>
  );
}

interface DocDetailsProps {
  /** The line to click, which says what opens. */
  title: string;
  children: ReactNode;
}

/**
 * A part of a page folded away until its title is clicked, in the FAQ's
 * style: a bordered box whose title line opens it (native `<details>`).
 */
export function DocDetails({ title, children }: DocDetailsProps) {
  return (
    <details className="group border-border rounded-lg border px-4">
      <DetailsSummary>{title}</DetailsSummary>
      <div className="text-foreground pb-4">{children}</div>
    </details>
  );
}
